/**
 * The rules behind the agency / agent registration step, as pure functions.
 *
 * Ported from the le-portal-ui-v3 prototype's `agency-registration` step
 * (src/LoginFlow.tsx). After the agency address is verified, a person whose agency or
 * own record is not on file is asked for the missing details before entering the portal.
 */
import { z } from 'zod';
import { firstError, firstErrorPerField, requiredText } from '@/utils/formSchemas';
import { isAssignedCountry, normalizeCountry } from '@/utils/countryCodes';
import { formatUSPhone, internationalPhoneSchema, usPhoneSchema } from '@/utils/phoneUtils';
import type { AgencyOnFile, AgentOnFile } from './api/contracts';

export interface AgencyDetails {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  /** ISO 3166-1 alpha-2 code, such as `US`. */
  country: string;
  phone: string;
  jurisdiction: string;
}

export interface AgentDetails {
  displayName: string;
  titleRank: string;
  role: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/** Which sections the registration step has to ask for. */
export interface RegistrationNeeds {
  agency: boolean;
  agent: boolean;
  /** Agent fields already on file, filled in and editable. */
  agentOnFile?: AgentOnFile;
  /** Agency fields already on file, shown read-only. */
  agencyOnFile?: AgencyOnFile;
}

export const EMPTY_AGENCY_DETAILS: AgencyDetails = {
  name: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  phone: '',
  jurisdiction: '',
};

export const EMPTY_AGENT_DETAILS: AgentDetails = {
  displayName: '',
  titleRank: '',
  role: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: '',
};

export type AgencyField = keyof AgencyDetails;

/** Every agency field, in form order. */
export const AGENCY_FIELDS = [
  'name',
  'street',
  'city',
  'state',
  'zip',
  'country',
  'phone',
  'jurisdiction',
] as const satisfies readonly AgencyField[];

// Listed rather than derived from the types, so a field added later is optional until
// someone decides otherwise. State and ZIP are optional: not every country uses them.
export const REQUIRED_AGENCY_FIELDS = [
  'name',
  'street',
  'city',
  'country',
  'phone',
  'jurisdiction',
] as const satisfies readonly AgencyField[];

export type RequiredAgencyField = (typeof REQUIRED_AGENCY_FIELDS)[number];

export const REQUIRED_AGENT_FIELDS = [
  'displayName',
  'titleRank',
  'role',
  'phone',
] as const satisfies readonly (keyof AgentDetails)[];

export type RequiredAgentField = (typeof REQUIRED_AGENT_FIELDS)[number];

export const AGENT_ADDRESS_FIELDS = ['street', 'city', 'state', 'zip', 'country'] as const;
export type AgentAddressFieldName = (typeof AGENT_ADDRESS_FIELDS)[number];
export const AGENT_FIELDS = [...REQUIRED_AGENT_FIELDS, ...AGENT_ADDRESS_FIELDS] as const;

/**
 * The agent form's starting values: the fields on file filled in, the rest blank. Unlike the
 * agency's, every agent field stays editable.
 */
export function initialAgentDetails(
  onFile: AgentOnFile | undefined,
  agencyCountryOnFile = '',
): AgentDetails {
  const details = { ...EMPTY_AGENT_DETAILS };
  for (const field of AGENT_FIELDS) {
    details[field] = onFile?.[field]?.trim() ?? '';
  }
  details.phone = displayPhone(details.phone, agencyCountryOnFile);
  return details;
}

/**
 * A stored phone as the form shows it: a `+1` number written `(XXX) XXX-XXXX` when the
 * agency's country on file is the US or Canada, anything else as stored. The country must be
 * on file, so it cannot later change and send the US-format number under another code.
 */
export function displayPhone(phone: string, countryOnFile: string): string {
  const trimmed = phone.trim();
  return /^\+1\d{10}$/.test(trimmed) && US_FORMAT_COUNTRIES.has(normalizeCountry(countryOnFile))
    ? formatUSPhone(trimmed.slice(2))
    : trimmed;
}

/**
 * Whether the agent changed anything from what ARS holds. With nothing on file, every
 * filled field counts as a change. The phone is compared as it would be saved, in E.164.
 */
export function agentDetailsChanged(
  details: AgentDetails,
  onFile: AgentOnFile | undefined,
  agencyCountry = '',
): boolean {
  const original = initialAgentDetails(onFile);
  const saved = (phone: string) => toE164(phone, agencyCountry) ?? phone.trim();
  const coreChanged = REQUIRED_AGENT_FIELDS.some((field) =>
    field === 'phone'
      ? saved(details.phone) !== saved(original.phone)
      : details[field].trim() !== original[field],
  );
  // ARS cannot clear a saved address: an all-blank address means keep, not replace.
  return coreChanged || (AGENT_ADDRESS_FIELDS.some((field) => details[field].trim() !== '') &&
    AGENT_ADDRESS_FIELDS.some((field) =>
      field === 'country'
        ? normalizeCountry(details[field]) !== normalizeCountry(original[field])
        : details[field].trim() !== original[field],
    ));
}

/** The agency fields already on file, which the form shows read-only. */
export function lockedAgencyFields(onFile: AgencyOnFile | undefined): AgencyField[] {
  return AGENCY_FIELDS.filter((field) => (onFile?.[field]?.trim() ?? '') !== '');
}

/** The agency form's starting values: the fields on file filled in, the rest blank. */
export function initialAgencyDetails(onFile: AgencyOnFile | undefined): AgencyDetails {
  const details = { ...EMPTY_AGENCY_DETAILS };
  for (const field of lockedAgencyFields(onFile)) {
    details[field] = onFile![field]!.trim();
  }
  details.phone = displayPhone(details.phone, details.country);
  return details;
}

/** Shown under any blank required field; generic because the field's label sits right above it. */
export const REQUIRED_FIELD_MESSAGE = 'This field is required.';

/** Shown under a country that is filled in but is not an assigned ISO 3166-1 alpha-2 code. */
export const INVALID_COUNTRY_MESSAGE = 'Choose a two-letter country code from the list, such as US.';

/** Each listed field required to be non-blank, keyed in the listed (form) order. */
function requiredFieldsShape<K extends string>(fields: readonly K[]) {
  return Object.fromEntries(
    fields.map((field) => [field, requiredText(REQUIRED_FIELD_MESSAGE)]),
  ) as Record<K, ReturnType<typeof requiredText>>;
}

// Overriding `country` in place keeps it at its form-order position in the shape.
export const agencyDetailsSchema = z.object({
  ...requiredFieldsShape(REQUIRED_AGENCY_FIELDS),
  // ARS accepts only assigned ISO 3166-1 alpha-2 codes, in any case and with surrounding space.
  country: requiredText(REQUIRED_FIELD_MESSAGE).refine(isAssignedCountry, INVALID_COUNTRY_MESSAGE),
});

export const agentDetailsSchema = z.object({
  ...requiredFieldsShape(REQUIRED_AGENT_FIELDS),
  street: z.string().trim().max(256).default(''),
  city: z.string().trim().max(256).default(''),
  state: z.string().trim().max(256).default(''),
  zip: z.string().trim().max(20).default(''),
  country: z.string().trim().refine(
    (country) => country === '' || isAssignedCountry(country),
    INVALID_COUNTRY_MESSAGE,
  ).transform(normalizeCountry).default(''),
});

/** Countries whose numbers are written `(XXX) XXX-XXXX` (the +1 North American plan). */
const US_FORMAT_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA']);

const PHONE_EXTENSION_PATTERN = /(?:ext(?:ension)?\.?|[x#;])/i;

export type PhoneFormat = 'us' | 'international';

/**
 * How a phone number is written: international when it starts with `+` or the agency is
 * outside North America, otherwise the US format case creation uses. A blank or unknown
 * country keeps the US format.
 */
export function phoneFormatFor(phone: string, country: string): PhoneFormat {
  if (phone.trimStart().startsWith('+')) {
    return 'international';
  }
  return isAssignedCountry(country) && !US_FORMAT_COUNTRIES.has(normalizeCountry(country))
    ? 'international'
    : 'us';
}

/** The international form ARS recommends: a leading `+`, then digits and single spaces. */
function formatInternationalPhoneInput(value: string): string {
  const extension = PHONE_EXTENSION_PATTERN.exec(value);
  const number = extension ? value.slice(0, extension.index).trimEnd() : value;
  const rest = number
    .replace(/[-.()]/g, ' ')
    .replace(/[^\d\s]/g, '')
    .replace(/\s+/g, ' ')
    .trimStart()
    .replace(/^((?:\d\s*){15}).+$/, '$1');
  return rest === '' && !value.includes('+') ? '' : `+${rest}`;
}

/** Formats a phone number as it is typed, by {@link phoneFormatFor}. */
export function formatPhoneInput(value: string, country: string): string {
  if (phoneFormatFor(value, country) === 'international') {
    return formatInternationalPhoneInput(value);
  }
  const digits = value.split(PHONE_EXTENSION_PATTERN, 1)[0].replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return formatUSPhone(national.slice(0, 10));
}

/** Why a filled-in phone number is not valid for its format, if it is not. */
function phoneFormatError(phone: string, country: string): string | undefined {
  const format = phoneFormatFor(phone, country);
  const schema = format === 'us' ? usPhoneSchema : internationalPhoneSchema;
  const result = schema.safeParse(phone);
  if (!result.success) {
    return firstError(result.error);
  }
  if (toE164(phone, country) === undefined) {
    return format === 'us' ? US_E164_MESSAGE : INTERNATIONAL_E164_MESSAGE;
  }
  return undefined;
}

/** E.164: a `+`, a country code that does not start with 0, and at most 15 digits in all. */
const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

/** Shown under a US-format number that cannot be stored in E.164, such as one with an extension. */
export const US_E164_MESSAGE = 'Enter a 10-digit phone number without an extension.';

/** Shown under an international number whose country code is not valid. */
export const INTERNATIONAL_E164_MESSAGE = 'Enter the number with its country code, such as +44 20 7946 0000.';

/**
 * A phone number as ARS stores it: E.164, such as `+12065550100`. A US-format number must be
 * exactly ten digits, saved under country code 1. Undefined when the number
 * cannot be written that way.
 */
export function toE164(phone: string, country: string): string | undefined {
  if (!/^\+?[\d\s().-]+$/.test(phone.trim())) {
    return undefined;
  }
  const digits = phone.replace(/\D/g, '');
  let e164: string;
  if (phoneFormatFor(phone, country) === 'international') {
    e164 = `+${digits}`;
  } else if (digits.length === 10) {
    e164 = `+1${digits}`;
  } else {
    return undefined;
  }
  return E164_PATTERN.test(e164) ? e164 : undefined;
}

/** Agent editor rules reuse registration validation and normalize the submitted phone. */
export function agentProfileFormSchema(country: string) {
  return agentDetailsSchema.extend({
    street: agentDetailsSchema.shape.street.unwrap(),
    city: agentDetailsSchema.shape.city.unwrap(),
    state: agentDetailsSchema.shape.state.unwrap(),
    zip: agentDetailsSchema.shape.zip.unwrap(),
    country: agentDetailsSchema.shape.country.unwrap(),
    phone: agentDetailsSchema.shape.phone.transform((phone, context) => {
      const message = phoneFormatError(phone, country);
      const normalized = toE164(phone, country);
      if (message || normalized === undefined) {
        context.addIssue({ code: 'custom', message: message ?? INTERNATIONAL_E164_MESSAGE });
        return z.NEVER;
      }
      return normalized;
    }),
  });
}

function fieldErrors<K extends string>(
  schema: z.ZodObject,
  details: unknown,
): Partial<Record<K, string>> {
  const result = schema.safeParse(details);
  return result.success ? {} : (firstErrorPerField(result.error) as Partial<Record<K, string>>);
}

/**
 * Adds the phone format error, if any, to the required-field errors, keeping form order. A
 * blank phone keeps its required-field message.
 */
function withPhoneError<K extends string>(
  fields: readonly K[],
  errors: Partial<Record<K, string>>,
  phoneField: K,
  phoneError: () => string | undefined,
): Partial<Record<K, string>> {
  const phone = errors[phoneField] ?? phoneError();
  const ordered: Partial<Record<K, string>> = {};
  for (const field of fields) {
    const message = field === phoneField ? phone : errors[field];
    if (message !== undefined) {
      ordered[field] = message;
    }
  }
  return ordered;
}

/**
 * The message for each agency field that is blank or invalid, in form order. A phone already
 * on file is locked, so its format is not checked.
 */
export function agencyFieldErrors(
  details: AgencyDetails,
  locked: readonly AgencyField[] = [],
): Partial<Record<keyof AgencyDetails, string>> {
  return withPhoneError(
    REQUIRED_AGENCY_FIELDS,
    fieldErrors(agencyDetailsSchema, details),
    'phone',
    () => (locked.includes('phone') ? undefined : phoneFormatError(details.phone, details.country)),
  );
}

/**
 * The message for each agent field that is blank or invalid, in form order. The phone is
 * checked against the agency's country.
 */
export function agentFieldErrors(
  details: AgentDetails,
  agencyCountry = '',
): Partial<Record<keyof AgentDetails, string>> {
  return withPhoneError(
    AGENT_FIELDS,
    fieldErrors(agentDetailsSchema, details),
    'phone',
    () => phoneFormatError(details.phone, agencyCountry),
  );
}

/** The required agency fields that are still blank or invalid, in form order. */
export function missingAgencyFields(
  details: AgencyDetails,
  locked: readonly AgencyField[] = [],
): (keyof AgencyDetails)[] {
  return Object.keys(agencyFieldErrors(details, locked)) as (keyof AgencyDetails)[];
}

/** The required agent fields that are still blank or invalid, in form order. */
export function missingAgentFields(details: AgentDetails, agencyCountry = ''): (keyof AgentDetails)[] {
  return Object.keys(agentFieldErrors(details, agencyCountry)) as (keyof AgentDetails)[];
}

export function agencyDetailsAreComplete(
  details: AgencyDetails,
  locked: readonly AgencyField[] = [],
): boolean {
  return missingAgencyFields(details, locked).length === 0;
}

export function agentDetailsAreComplete(details: AgentDetails, agencyCountry = ''): boolean {
  return missingAgentFields(details, agencyCountry).length === 0;
}

/** Whether every section the step asks for has its required fields filled. */
export function registrationIsComplete(
  needs: RegistrationNeeds,
  agency: AgencyDetails,
  agent: AgentDetails,
): boolean {
  return (
    (!needs.agency || agencyDetailsAreComplete(agency, lockedAgencyFields(needs.agencyOnFile))) &&
    (!needs.agent || agentDetailsAreComplete(agent, agency.country))
  );
}

/** The domain part of an address, which is what identifies the agency. */
export function emailDomain(email: string): string {
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  return at < 0 ? '' : normalized.slice(at + 1);
}
